import { useEffect, useRef } from 'react';
import { invalidateCachedJson } from './portalQueryCache';

const TOOLS_CACHE_URL = '/api/tools';
const REVISION_URL = '/api/auth/platform-access-revision';
const PLATFORM_ACCESS_CHANGED = 'hk:platform-access-changed';

export function notifyPlatformAccessChanged(revision) {
  window.dispatchEvent(new CustomEvent(PLATFORM_ACCESS_CHANGED, { detail: { revision } }));
}

export function usePlatformAccessSync(enabled, knownRevision = 0) {
  const revisionRef = useRef(Number(knownRevision) || 0);

  useEffect(() => {
    revisionRef.current = Number(knownRevision) || 0;
  }, [knownRevision]);

  useEffect(() => {
    if (!enabled) return undefined;

    const poll = async () => {
      if (document.hidden) return;
      try {
        const response = await fetch(REVISION_URL, { credentials: 'include' });
        if (!response.ok) return;
        const payload = await response.json();
        const nextRevision = Number(payload?.revision) || 0;
        if (nextRevision <= 0) return;

        if (revisionRef.current <= 0) {
          revisionRef.current = nextRevision;
          return;
        }

        if (nextRevision !== revisionRef.current) {
          revisionRef.current = nextRevision;
          invalidateCachedJson(TOOLS_CACHE_URL);
          notifyPlatformAccessChanged(nextRevision);
        }
      } catch {
        // ignore transient polling errors
      }
    };

    void poll();
    const id = window.setInterval(poll, 15_000);
    const onVis = () => {
      if (!document.hidden) void poll();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled]);
}

export function usePlatformAccessChanged(handler) {
  useEffect(() => {
    if (typeof handler !== 'function') return undefined;
    const onChange = (event) => handler(event?.detail?.revision);
    window.addEventListener(PLATFORM_ACCESS_CHANGED, onChange);
    return () => window.removeEventListener(PLATFORM_ACCESS_CHANGED, onChange);
  }, [handler]);
}

export { TOOLS_CACHE_URL };
